import React, { useEffect, useState } from "react";
import {
    getQuestions,
    createQuestion,
    createAnswer,
    deleteQuestion,
} from "../../api/faq";
import { formatDate } from "../../utils/date";
import SearchFilterBar from "../../components/common/board/SearchFilterBar";
import BoardPagination from "../../components/common/board/BoardPagination";
import PageHeader from "../../components/common/ui/PageHeader";

import {
    MessageCircle,
    CheckCircle,
    HelpCircle,
    PenTool,
    ArrowLeft,
    Lock,
    Trash2,
    Send,
} from "lucide-react";
import BoardToolbar from "../../components/common/board/BoardToolbar";

/* =========================================================
   MAIN
========================================================= */
export default function FAQPage() {
    const [view, setView] = useState("list"); // list | create | detail
    const [questions, setQuestions] = useState([]);
    const [selectedId, setSelectedId] = useState(null);
    const [loading, setLoading] = useState(true);
    const [user, setUser] = useState(null);
    const [searchField, setSearchField] = useState("title");
    const [dateFilter, setDateFilter] = useState("all");


    // list state
    const [q, setQ] = useState("");
    const [page, setPage] = useState(1);
    const [pageSize] = useState(8);
    const [total, setTotal] = useState(0);


    useEffect(() => {
        setUser(JSON.parse(localStorage.getItem("user")));
    }, []);

    useEffect(() => {
        fetchList();
    }, [page]);

    const fetchList = async () => {
        setLoading(true);
        try {
            const res = await getQuestions({
                page,
                page_size: pageSize,
                search: q,
            });
            console.log("FAQ res.data =", res.data);

            // ✅ 페이징 응답 강제 (results / count)
            const results = res.data?.results ?? [];
            const count = res.data?.count ?? results.length;

            setQuestions(results);
            setTotal(count);
        } finally {
            setLoading(false);
        }
    };


    const selectedQuestion = questions.find((q) => q.id === selectedId);

    const stats = {
        total,
        solved: questions.filter((q) => q.answers.length > 0).length,
        pending: questions.filter((q) => q.answers.length === 0).length,
    };

    return (
        <div className="min-h-screen bg-gray-50/50 p-6 pb-32">
            <PageHeader>P6ix 해결사</PageHeader>

            {/* ===== Summary ===== */}
            {user?.is_superuser && (
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-8">
                    <SummaryCard
                        icon={<MessageCircle size={22} />}
                        label="전체 질문"
                        value={stats.total}
                    />
                    <SummaryCard
                        icon={<CheckCircle size={22} />}
                        label="답변완료"
                        value={stats.solved}
                        color="green"
                    />
                    <SummaryCard
                        icon={<HelpCircle size={22} />}
                        label="답변대기"
                        value={stats.pending}
                        color="orange"
                    />
                </div>
            )}

            {/* ===== Main Box ===== */}
            <div className="page-box min-h-[750px] flex flex-col">
                {/* ✅ Toolbar는 여기 */}
                <div className="flex-1">
                {view === "list" && (
                    <BoardToolbar
                        left={
                            <div className="flex items-center gap-2">
                                <input
                                    className="input-base w-64"
                                    placeholder="질문 검색"
                                    value={q}
                                    onChange={(e) => setQ(e.target.value)}
                                    onKeyDown={(e) => {
                                        if (e.key === "Enter") {
                                            setPage(1);
                                            fetchList();
                                        }
                                    }}
                                />

                                <button
                                    className="btn-search"
                                    onClick={() => {
                                        setPage(1);
                                        fetchList();
                                    }}
                                >
                                    검색
                                </button>
                            </div>
                        }
                        right={
                            <button
                                onClick={() => setView("create")}
                                className="btn-create flex items-center gap-2"
                            >
                                <PenTool size={16} /> 새 질문하기
                            </button>
                        }
                    />

                )}

                {view === "list" && (
                    <FAQListView
                        loading={loading}
                        questions={questions}
                        q={q}
                        pageSize={pageSize}
                        onChangeQ={setQ}
                        onSearch={() => {
                            setPage(1);
                            fetchList();
                        }}
                        page={page}
                        total={total}
                        onPageChange={setPage}
                        onSelect={(id) => {
                            setSelectedId(id);
                            setView("detail");
                        }}
                    />
                )}

                {view === "create" && (
                    <FAQCreateView
                        onCancel={() => setView("list")}
                        onSaved={() => {
                            fetchList();
                            setView("list");
                        }}
                    />
                )}

                {view === "detail" && selectedQuestion && (
                    <FAQDetailView
                        user={user}
                        question={selectedQuestion}
                        onBack={() => setView("list")}
                        onRefresh={fetchList}
                    />
                )}
                </div>
            </div>

            {/* ===== Help Banner ===== */}
            <div className="mt-8 rounded-2xl bg-gradient-to-r from-gray-900 to-slate-800 p-8 text-white text-center shadow-lg">
                <h3 className="text-xl font-bold mb-2">도움이 더 필요하신가요?</h3>
                <p className="text-white/80 text-sm">
                    시스템 이용 중 발생하는 문제에 대해 관리자에게 문의해주세요.
                </p>
                <div className="mt-4 pt-4 border-t border-white/10 text-sm font-semibold">
                    <p>이메일 : cpmlee@p6ix.co.kr</p>
                    <p>연락처 : 010-5871-5452</p>
                </div>
            </div>
        </div>
    );
}

/* =========================================================
   COMPONENTS
========================================================= */

const SummaryCard = ({ icon, label, value, color = "blue" }) => {
    const colors = {
        blue: "bg-blue-50 text-blue-600",
        green: "bg-green-50 text-green-600",
        orange: "bg-orange-50 text-orange-600",
    };

    return (
        <div className="bg-white p-5 rounded-xl border flex items-center gap-4">
            <div className={`p-3 rounded-lg ${colors[color]}`}>{icon}</div>
            <div>
                <p className="text-xs text-gray-500 font-semibold">{label}</p>
                <p className="text-xl font-bold">{value}</p>
            </div>
        </div>
    );
};

function FAQListView({
    loading,
    questions,
    q,
    onChangeQ,
    onSearch,
    pageSize,
    page,
    total,
    onPageChange,
    onSelect,
}) {
    return (
        <>
            {/* <SearchFilterBar
                q={q}
                onChangeQ={onChangeQ}
                onSearch={onSearch}
                placeholder="질문 검색"
            /> */}

            <div className="divide-y mt-4">
                {loading && <div className="p-6 text-center">로딩 중...</div>}
                {!loading && questions.length === 0 && (
                    <div className="p-6 text-center text-gray-400">
                        등록된 질문이 없습니다.
                    </div>
                )}
                {questions.map((q) => (
                    <div
                        key={q.id}
                        onClick={() => onSelect(q.id)}
                        className="p-4 cursor-pointer hover:bg-gray-50"
                    >
                        <div className="flex items-center gap-2">
                            {!q.is_public && <Lock size={12} />}
                            <span className="font-medium">{q.title}</span>
                        </div>
                        <div className="text-xs text-gray-400 mt-1">
                            {q.created_by_username} · {formatDate(q.created_at)}
                        </div>
                    </div>
                ))}
            </div>

            <BoardPagination
                page={page}
                pageSize={pageSize}
                total={total}
                onPageChange={onPageChange}
            />
        </>
    );
}

function FAQCreateView({ onCancel, onSaved }) {
    const [title, setTitle] = useState("");
    const [content, setContent] = useState("");

    const submit = async () => {
        if (!title || !content) return;
        await createQuestion({ title, content });
        onSaved();
    };

    return (
        <div className="space-y-4">
            <button onClick={onCancel} className="flex items-center gap-1 text-sm">
                <ArrowLeft size={14} /> 목록으로
            </button>

            <input
                className="input-base"
                placeholder="제목"
                value={title}
                onChange={(e) => setTitle(e.target.value)}
            />
            <textarea
                className="input-base h-40"
                placeholder="내용"
                value={content}
                onChange={(e) => setContent(e.target.value)}
            />

            <div className="flex justify-end gap-2">
                <button className="btn-cancel" onClick={onCancel}>
                    취소
                </button>
                <button className="btn-create" onClick={submit}>
                    등록
                </button>
            </div>
        </div>
    );
}

function FAQDetailView({ user, question, onBack, onRefresh }) {
    const [reply, setReply] = useState("");
    const isAdmin = user?.is_superuser;

    return (
        <div className="space-y-4">
            <button onClick={onBack} className="flex items-center gap-1 text-sm">
                <ArrowLeft size={14} /> 목록으로
            </button>

            <div className="flex justify-between items-start">
                <h3 className="text-lg font-bold">{question.title}</h3>
                {isAdmin && (
                    <button
                        onClick={async () => {
                            if (window.confirm("삭제하시겠습니까?")) {
                                await deleteQuestion(question.id);
                                onBack();
                                onRefresh();
                            }
                        }}
                        className="text-red-500"
                    >
                        <Trash2 size={16} />
                    </button>
                )}
            </div>

            <div className="text-sm text-gray-500">
                {question.created_by_username} ·{" "}
                {formatDate(question.created_at)}
            </div>

            <div className="whitespace-pre-wrap">{question.content}</div>

            <hr />

            {question.answers.map((a) => (
                <div key={a.id} className="bg-gray-50 p-3 rounded">
                    <div className="text-xs text-gray-400 mb-1">
                        관리자 · {formatDate(a.created_at)}
                    </div>
                    {a.content}
                </div>
            ))}

            {isAdmin && (
                <div className="flex gap-2">
                    <textarea
                        className="input-base flex-1"
                        placeholder="답변 작성"
                        value={reply}
                        onChange={(e) => setReply(e.target.value)}
                    />
                    <button
                        className="btn-create"
                        onClick={async () => {
                            await createAnswer({
                                question: question.id,
                                content: reply,
                            });
                            setReply("");
                            onRefresh();
                        }}
                    >
                        <Send size={16} />
                    </button>
                </div>
            )}
        </div>
    );
}
